import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import { useId } from 'react';
import { cx } from './cx';

export function IconButton({ label, className, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cx(
        'inline-flex size-9 shrink-0 items-center justify-center rounded-full text-text-2 transition hover:bg-surface-2 hover:text-text-1 disabled:opacity-40',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

/** Escolha exclusiva em pílulas (role=radiogroup). */
export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  label,
  size = 'md',
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: ReactNode }[];
  label: string;
  size?: 'md' | 'lg';
}) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex flex-wrap gap-1 rounded-full bg-surface-2 p-1">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={cx(
            'rounded-full font-medium transition',
            size === 'lg' ? 'h-12 px-5 text-[16px]' : 'h-8 px-3.5 text-sm',
            value === o.value ? 'bg-surface-1 text-text-1 shadow-[0_1px_2px_rgb(0_0_0/0.08)]' : 'text-text-2 hover:text-text-1',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Switch({ checked, onChange, label, description }: { checked: boolean; onChange: (v: boolean) => void; label: string; description?: string }) {
  const id = useId();
  return (
    <div className="flex items-start justify-between gap-4">
      <label htmlFor={id} className="min-w-0">
        <span className="block font-medium">{label}</span>
        {description && <span className="mt-0.5 block text-sm text-text-2">{description}</span>}
      </label>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={cx('relative mt-0.5 h-7 w-12 shrink-0 rounded-full transition', checked ? 'bg-accent' : 'bg-border-strong')}
      >
        <span className={cx('absolute top-0.5 size-6 rounded-full bg-white shadow transition-[left] duration-150', checked ? 'left-[22px]' : 'left-0.5')} />
      </button>
    </div>
  );
}

export function Checkbox({ label, description, className, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; description?: ReactNode }) {
  const id = useId();
  return (
    <div className={cx('flex items-start gap-3', className)}>
      <input id={id} type="checkbox" className="mt-0.5 size-5 shrink-0 cursor-pointer rounded accent-[var(--accent)]" {...rest} />
      <label htmlFor={id} className="cursor-pointer text-[15px] leading-snug">
        {label}
        {description && <span className="mt-1 block text-sm text-text-2">{description}</span>}
      </label>
    </div>
  );
}

/** `marker`: um ponto intermédio que conta (ex.: o ritmo da semana), desenhado como um traço na barra. */
export function ProgressBar({ value, max, label, className, marker }: { value: number; max: number; label: string; className?: string; marker?: number }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  const markerPct = marker != null && max > 0 && marker > 0 && marker < max ? (marker / max) * 100 : null;
  return (
    <div role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={max} aria-valuenow={value} className={cx('relative h-1.5 overflow-hidden rounded-full bg-surface-2', className)}>
      <div className="h-full rounded-full bg-accent transition-[width] duration-300 ease-nolens" style={{ width: `${pct}%` }} />
      {markerPct != null && <div aria-hidden className="absolute inset-y-0 w-0.5 -translate-x-1/2 bg-text-3" style={{ left: `${markerPct}%` }} />}
    </div>
  );
}

/** Menu simples sobre a Popover API nativa. */
/** `label` é obrigatório: o trigger é muitas vezes só um ícone e precisa de nome acessível. */
export function Menu({ id, label, trigger, children }: { id: string; label: string; trigger: ReactNode; children: ReactNode }) {
  return (
    <>
      <button type="button" popoverTarget={id} aria-haspopup="menu" aria-label={label} title={label} className="inline-flex">
        {trigger}
      </button>
      <div id={id} popover="auto" role="menu" className="m-0 min-w-52 rounded-card border border-border bg-surface-1 p-1.5 text-text-1 shadow-xl [position-area:bottom_span-left] [position-try-fallbacks:flip-block]">
        {children}
      </div>
    </>
  );
}

export function MenuItem({ onClick, children, tone = 'neutral', popoverId }: { onClick: () => void; children: ReactNode; tone?: 'neutral' | 'critical'; popoverId: string }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={() => {
        (document.getElementById(popoverId) as HTMLElement & { hidePopover?: () => void })?.hidePopover?.();
        onClick();
      }}
      className={cx('flex w-full items-center gap-2 rounded-input px-3 py-2 text-left text-sm hover:bg-surface-2', tone === 'critical' && 'text-critical')}
    >
      {children}
    </button>
  );
}
